import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ScrapeRun, type ScrapeTarget } from "../lib/api";

// The scraper can run for minutes (Chromium boot + a 3-7s delay between items)
// while the API Lambda is capped at 60s, so the trigger cannot answer with the
// result. The button stays in a loading state and this hook polls the run row
// until the scraper closes it — the UX is synchronous, the transport is not.

const POLL_INTERVAL_MS = 3_000;
// Bounds the poll at the scraper Lambda's own 10min timeout. Past that the run
// cannot still be alive, so a row stuck on "running" means the Lambda died
// without closing it — surfaced as a timeout rather than an infinite spinner.
const POLL_TIMEOUT_MS = 10 * 60 * 1_000;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const pollUntilSettled = async (runId: string): Promise<ScrapeRun> => {
  const deadline = Date.now() + POLL_TIMEOUT_MS;
  while (Date.now() < deadline) {
    await sleep(POLL_INTERVAL_MS);
    const run = await api.inbox.scrapeRun(runId);
    if (run.status !== "running") return run;
  }
  throw new Error("SCRAPE_RUN_TIMEOUT");
};

export const useScrapeNow = () => {
  const qc = useQueryClient();
  // Which target is in flight, so the page can show a spinner on the button the
  // user actually pressed rather than on both.
  const [runningTarget, setRunningTarget] = useState<ScrapeTarget | null>(null);

  const mutation = useMutation<ScrapeRun, Error, ScrapeTarget>({
    mutationFn: async (target) => {
      setRunningTarget(target);
      const run = await api.inbox.scrapeNow(target);
      return pollUntilSettled(run.runId);
    },
    onSettled: () => {
      setRunningTarget(null);
      // A finished run may have written new engagements.
      qc.invalidateQueries({ queryKey: ["inbox"] });
    },
  });

  return { ...mutation, runningTarget };
};
